<template>
  <q-card
    v-bind="$attrs"
    data-test-id="one-time-code-login"
  >
    <q-form
      ref="formRef"
    >
      <!-- Step 1: Choose email or phone -->
      <div v-if="!codeSent">
        <div class="q-mb-lg">
          <tab-toggle-button
            v-if="showBoth"
            v-model="tabContext"
            class="full-width q-mx-auto"
            spread
            :buttons="buttons"
            :loading="loading"
            :padding="tabButtonPadding"
            :class="classes"
            @select-tab="(value) => tabContext = value"
          />
        </div>

        <q-input
          v-if="tabContext === 'email'"
          v-model.trim="emailAddress"
          outlined
          :label="$t('kiosk.email')"
          type="email"
          :rules="[
            val => required.$validator(val) || $t('form.errors.required'),
            val => email.$validator(val?.toLowerCase()) || $t('form.errors.emailNotValid')
          ]"
          lazy-rules
          :disable="loading"
          class="q-mb-sm hide-bottom-space thick-border"
          :class="classes"
        />

        <q-input
          v-else-if="tabContext === 'phone'"
          v-model.trim="phoneNumber"
          outlined
          type="tel"
          :label="$t('kiosk.phone')"
          :mask="$t('form.masks.phoneNumber')"
          :rules="[val => required.$validator(val) || $t('form.errors.required')]"
          lazy-rules
          :disable="loading"
          class="q-mb-sm hide-bottom-space thick-border"
          :class="classes"
        />
      </div>

      <!-- Step 2: Verification code -->
      <div v-else>
        <q-input
          v-model.trim="verificationCode"
          outlined
          type="tel"
          :label="$t('kiosk.enterYourCode')"
          :rules="[val => required.$validator(val) || $t('form.errors.required')]"
          lazy-rules
          :disable="loading"
          class="q-mb-sm hide-bottom-space thick-border"
          :class="classes"
        />
      </div>

      <!-- Action button -->
      <q-btn
        :label="codeSent ? $t('auth.actions.login') : $t('kiosk.actions.sendCode')"
        :padding="buttonPadding"
        color="primary"
        :text-color="$getColor(currentSite, 'primary')"
        class="full-width q-mb-lg"
        :class="classes"
        :loading="loading"
        @click="submit"
      />

      <div
        v-if="codeSent"
        class="text-center"
        :style="{ 'font-size': $isLandscapeView() ? '1.5vw' : '1.5vh' }"
      >
        {{ $t('kiosk.resendCodePrompt') }}

        <template v-if="tabContext === 'email'">
          {{ $t('kiosk.checkSpamPrompt') }}
        </template>

        <q-btn
          :label="resendCodeLabel"
          color="responsive"
          :flat="resendCodeCount > 0"
          :outline="resendCodeCount === 0"
          class="text-capitalize full-width text-weight-light q-my-lg"
          :class="classes"
          :disable="resendCodeCount > 0"
          @click="resendCode"
        />
      </div>

      <!-- Phone number disclaimer -->
      <q-banner
        v-if="tabContext === 'phone' && !codeSent"
        rounded
        class="bg-responsive-grey-1 border-responsive-grey-1 text-caption text-responsive-grey-7 q-mb-md q-px-sm disclaimer-text text-left"
        :style="{ 'font-size': $isLandscapeView() ? '0.9vw' : '0.9vh' }"
      >
        <span v-html="$t('kiosk.smsDisclaimer', { siteName: currentSite.name, privacyPolicyUrl: currentSite.path('/privacy-policy') })" />
      </q-banner>
    </q-form>
  </q-card>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { ONE_TIME_CODE_LOGIN_TYPES } from 'src/models/circuit/Circuit'
import User from 'src/models/user/User'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'
import TabToggleButton from 'src/modules/register/components/TabToggleButton'

export default {
  name: 'OneTimeCodeLogin',
  components: {
    TabToggleButton
  },
  mixins: [FormValidationMixin],
  inject: ['executeRecaptchaAsync'],
  configuration: ['currentSite', 'currentCircuit'],
  props: {
    requestCode: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonPadding: {
      type: String,
      required: false,
      default: null
    },
    tabButtonPadding: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['code-sent', 'login-success'],
  data () {
    return {
      loading: false,
      tabContext: this.currentCircuit.kioskOptions.default_one_time_code_login_type,
      codeSent: false,
      emailAddress: '',
      phoneNumber: '',
      verificationCode: '',
      resendCodeCount: 30,
      resendTimer: null
    }
  },
  computed: {
    buttons () {
      const isPhoneTabContext = this.tabContext === 'phone'
      const isEmailTabContext = this.tabContext === 'email'
      return [
        {
          label: this.$t('kiosk.phone'),
          value: 'phone',
          color: isPhoneTabContext ? 'primary' : undefined,
          textColor: isPhoneTabContext ? this.$getColor(this.currentSite, 'primary') : 'responsive',
          class: isPhoneTabContext ? 'text-weight-bold text-body1 size-lg' : 'text-weight-regular text-body1 size-lg'
        },
        {
          label: this.$t('kiosk.email'),
          value: 'email',
          color: isEmailTabContext ? 'primary' : undefined,
          textColor: isEmailTabContext ? this.$getColor(this.currentSite, 'primary') : 'responsive',
          class: isEmailTabContext ? 'text-weight-bold text-body1 size-lg' : 'text-weight-regular text-body1 size-lg'
        }
      ]
    },
    showBoth () {
      if (this.codeSent) {
        return false
      }
      // add logic to check if both email and phone are valid
      return this.currentCircuit.kioskOptions.one_time_code_login_type === ONE_TIME_CODE_LOGIN_TYPES.BOTH
    },
    classes () {
      const classes = []
      if (this.$kiosk) {
        classes.push('kiosk')
      }
      if (this.$isLandscapeView()) {
        classes.push('landscape')
      } else {
        classes.push('portrait')
      }
      return classes.join(' ')
    },
    resendCodeLabel () {
      return this.resendCodeCount > 0 ? `${this.$t('kiosk.actions.resendCode')} (${this.resendCodeCount}s)` : this.$t('kiosk.actions.resendCode')
    }
  },
  watch: {
    requestCode: {
      handler (newVal) {
        if (newVal === false) {
          this.codeSent = false
        }
      },
      immediate: true
    },
    tabContext: {
      handler () {
        this.emailAddress = ''
        this.phoneNumber = ''
      }
    },
    codeSent: {
      handler (newVal) {
        if (newVal) {
          this.startResendCountdown()
        } else {
          this.stopResendCountdown()
        }
      }
    }
  },
  created () {
    _.extend(this, { email, required, ONE_TIME_CODE_LOGIN_TYPES })
  },
  unmounted () {
    this.stopResendCountdown()
  },
  methods: {
    async sendOtpCode (recaptchaToken = '') {
      this.loading = true
      const otpMutation = this.tabContext === 'email'
        ? User.createOtpCode({ email: this.emailAddress, recaptchaToken })
        : User.createOtpCode({ phoneNumber: this.phoneNumber, recaptchaToken })

      const { data, errors } = await otpMutation
      this.loading = false

      if (errors && errors.length > 0) {
        this.$mh.showErrors(errors)
        return false
      }

      if (data && data.success) {
        this.$emit('code-sent', true)
        this.codeSent = true
        this.startResendCountdown()
        return true
      } else {
        this.$mh.showErrors([{ description: 'Failed to send verification code. Please try again.' }])
        return false
      }
    },
    async submit () {
      const isValid = await this.$refs.formRef.validate()
      if (!isValid) return

      let recaptchaToken = ''
      if (this.currentSite.recaptchaRequired) {
        recaptchaToken = await this.executeRecaptchaAsync()
      }

      if (!this.codeSent) {
        // Step 1 logic - send OTP code
        await this.sendOtpCode(recaptchaToken)
      } else {
        // Step 2 logic
        this.loading = true
        const otpMutation = this.tabContext === 'email'
          ? User.verifyOtpCode({ email: this.emailAddress, otpCode: this.verificationCode, recaptchaToken })
          : User.verifyOtpCode({ phoneNumber: this.phoneNumber, otpCode: this.verificationCode, recaptchaToken })
        const { data, errors } = await otpMutation
        this.loading = false

        if (errors && errors.length > 0) {
          console.error('Verification errors:', errors)
          this.$mh.showErrors(errors)
          return
        }

        if (data && data.success) {
          try {
            this.$emit('login-success', data?.user?.id)
          } catch (error) {
            console.error('Error completing authentication:', error)
            this.$mh.showErrors([{ description: 'Authentication completed but there was an error setting up your session. Please try again.' }])
          }
        } else {
          this.$mh.showErrors([{ description: 'Verification failed. Please check your code and try again.' }])
        }
      }
    },
    async sendCode () {
      let recaptchaToken = ''
      if (this.currentSite.recaptchaRequired) {
        recaptchaToken = await this.executeRecaptchaAsync()
      }
      return await this.sendOtpCode(recaptchaToken)
    },
    async verifyCode () {
      let recaptchaToken = ''
      if (this.currentSite.recaptchaRequired) {
        recaptchaToken = await this.executeRecaptchaAsync()
      }

      const otpMutation = this.tabContext === 'email'
        ? User.verifyOtpCode({ email: this.emailAddress, otpCode: this.verificationCode, recaptchaToken })
        : User.verifyOtpCode({ phoneNumber: this.phoneNumber, recaptchaToken })
      const { data, errors } = await otpMutation

      if (errors && errors.length > 0) {
        console.error('Verification errors:', errors)
        this.$mh.showErrors(errors)
        return false
      }

      if (data && data.success) {
        try {
          this.$emit('login-success', data?.user?.id)
          return true
        } catch (error) {
          console.error('Error completing authentication:', error)
          this.$mh.showErrors([{ description: 'Authentication completed but there was an error setting up your session. Please try again.' }])
          return false
        }
      } else {
        this.$mh.showErrors([{ description: 'Verification failed. Please check your code and try again.' }])
        return false
      }
    },
    async resendCode () {
      if (this.resendCodeCount > 0) return
      await this.sendOtpCode()
    },

    startResendCountdown () {
      this.stopResendCountdown()
      this.resendCodeCount = 30

      this.resendTimer = setInterval(() => {
        this.resendCodeCount--
        if (this.resendCodeCount <= 0) {
          this.stopResendCountdown()
        }
      }, 1000)
    },

    stopResendCountdown () {
      if (this.resendTimer) {
        clearInterval(this.resendTimer)
        this.resendTimer = null
      }
      this.resendCodeCount = 0
    }
  }
}
</script>
<style lang="scss" scoped>
/**
 * Kiosk specific styles
 */
.kiosk.landscape {
  font-size: 2vw;
}
.kiosk.portrait {
  font-size: 2vh;
}
.q-btn.kiosk.landscape {
  font-size: 2vw;
}
.q-btn.kiosk.portrait {
  font-size: 2vh;
}

.q-btn-group.kiosk.landscape {
  padding: 0.4vw;
}
.q-btn-group.kiosk.portrait {
  padding: 0.4vh;
}

:deep(.q-field--outlined.thick-border .q-field__control:before) {
    border-width: 3px;
  }
:deep(.q-btn-group .text-body1){
  font-size: 1em;
}
:deep(.kiosk.portrait .q-field__control){
  height: 6vh;
}
:deep(.kiosk.landscape .q-field__control){
  height: 5vw;
}
:deep(.kiosk.portrait.q-field--labeled .q-field__native){
  min-height: 6vh;
  font-size: 2vh;
}
:deep(.kiosk.landscape.q-field--labeled .q-field__native){
  min-height: 5vw;
  font-size: 2vw;
}
/* Disclaimer text styling */
.disclaimer-text {
  line-height: 1.2;
  font-weight: 400;

  a {
    text-decoration: underline;
    color: inherit;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
